import { lazy, Suspense, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";

const TrustStats = lazy(() => import("@/components/TrustStats"));
const PainPoints = lazy(() => import("@/components/PainPoints"));
const Services = lazy(() => import("@/components/Services"));
const VideoSection = lazy(() => import("@/components/VideoSection"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const FAQ = lazy(() => import("@/components/FAQ"));
const Contact = lazy(() => import("@/components/Contact"));
const Footer = lazy(() => import("@/components/Footer"));
const StickyMobileCTA = lazy(() => import("@/components/StickyMobileCTA"));
const LeadMagnets = lazy(() => import("@/components/LeadMagnets"));
const CityLinks = lazy(() => import("@/components/CityLinks"));
const NearYouBand = lazy(() => import("@/components/NearYouBand"));
const LearningCenter = lazy(() => import("@/components/LearningCenter"));



const jsonLd = {
  "@context": "https://schema.org",
  "@type": ["AccountingService", "ProfessionalService"],
  name: "RR Dental Accountants",
  alternateName: "RR Dental Accountants",
  description:
    "ACCA-certified specialist dental accountants for associates, therapists, hygienists, and practice owners across the UK. Expert tax planning, self-assessment tax returns, NHS pension advice, dental practice acquisitions, payroll, VAT, and bookkeeping.",
  url: "https://dentalaccountancy.co.uk",
  image: "https://dentalaccountancy.co.uk/og-image.png",
  logo: "https://dentalaccountancy.co.uk/og-image.png",
  telephone: "+447398888594",
  email: "info@dentalaccountancy.co.uk",
  foundingDate: "2011",
  founder: {
    "@type": "Person",
    name: "Rehan Razzaq",
    jobTitle: "Director & ACCA Chartered Certified Accountant",
    url: "https://www.linkedin.com/in/rehan-razzaq-acca",
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "76 St James Road",
    addressLocality: "Blackburn",
    addressRegion: "Lancashire",
    postalCode: "BB1 8ET",
    addressCountry: "GB",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: "53.7488",
    longitude: "-2.4818",
  },
  areaServed: [
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "City", name: "London" },
    { "@type": "City", name: "Manchester" },
    { "@type": "City", name: "Birmingham" },
    { "@type": "City", name: "Leeds" },
    { "@type": "City", name: "Liverpool" },
    { "@type": "City", name: "Bristol" },
    { "@type": "City", name: "Sheffield" },
    { "@type": "City", name: "Edinburgh" },
    { "@type": "City", name: "Glasgow" },
    { "@type": "City", name: "Cardiff" },
    { "@type": "City", name: "Blackburn" },
    { "@type": "City", name: "Lancashire" },
    { "@type": "City", name: "Bolton" },
    { "@type": "City", name: "Preston" },
    { "@type": "City", name: "Burnley" },
  ],
  priceRange: "££",
  paymentAccepted: "Bank Transfer, Direct Debit",
  currenciesAccepted: "GBP",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday", "Sunday"],
      opens: "10:00",
      closes: "16:00",
    },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "14",
    bestRating: "5",
  },
  review: [
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Lina K" },
      datePublished: "2025-09-12",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5", worstRating: "1" },
      reviewBody: "Rehan was amazing. He was so prompt, efficient and made me feel at ease!",
      publisher: { "@type": "Organization", name: "Google" },
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Sarra Jawad" },
      datePublished: "2025-05-09",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5", worstRating: "1" },
      reviewBody: "Great accountant! Knowledgable, fast, efficient, respectful. Answers all queries.",
      publisher: { "@type": "Organization", name: "Google" },
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Brian O'Neill" },
      datePublished: "2024-11-14",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5", worstRating: "1" },
      reviewBody: "Super easy to work with. Took care of everything A-Z, didn't have to lift a finger.",
      publisher: { "@type": "Organization", name: "Google" },
    },
  ],
  sameAs: [
    "https://www.instagram.com/r.raccountants",
    "https://www.linkedin.com/in/rehan-razzaq-acca",
    "https://wa.me/447398888594",
  ],
  knowsAbout: [
    "dental accountants",
    "accountants for dentists",
    "specialist dental accountants",
    "dental accounting",
    "accounting for dentists",
    "dental practice accounting",
    "dental practice accountant",
    "accountants for dental practices",
    "dental bookkeeping",
    "bookkeeping for dentists",
    "healthcare accountants",
    "tax planning for dentists",
    "NHS pension planning",
    "dental practice acquisitions",
    "self-assessment tax returns for dentists",
    "dental practice VAT",
    "dental practice payroll",
    "Making Tax Digital for dentists",
    "capital allowances dental equipment",
    "limited company for dentists",
    "IR35 for dental professionals",
  ],
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Professional Certification",
      name: "ACCA Chartered Certified Accountant",
      recognizedBy: {
        "@type": "Organization",
        name: "Association of Chartered Certified Accountants",
        url: "https://www.accaglobal.com/gb/en.html",
      },
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Professional Certification",
      name: "Xero Certified Advisor",
      recognizedBy: {
        "@type": "Organization",
        name: "Xero",
        url: "https://www.xero.com",
      },
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Specialist Dental Accountants Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Dental Associate, Therapist & Hygienist Tax Returns",
          description: "Self-assessment tax returns, expense optimisation, NHS pension advice, and tax planning for self-employed dental associates, therapists, and hygienists across the UK.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Dental Practice Owner Accounts & Tax Planning",
          description: "Full accounts preparation, corporation tax, payroll, VAT returns, management accounts, and strategic tax planning for dental practice owners.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Dental Practice Acquisitions & Sales",
          description: "Due diligence, business valuations, financial projections, bank finance applications, and structuring advice for buying or selling dental practices.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "NHS Pension Planning for Dentists",
          description: "Expert NHS pension annual allowance planning, contribution optimisation, and retirement planning for dental professionals.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Making Tax Digital Compliance for Dentists",
          description: "MTD-ready bookkeeping, Xero setup, digital record-keeping, and quarterly reporting for dental professionals.",
        },
      },
    ],
  },
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Do you specialise in accounting for dentists?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, RR Dental Accountants specialise exclusively in accounting for dental professionals across the UK, including associates, therapists, hygienists, practice owners, and those looking to acquire practices.",
      },
    },
    {
      "@type": "Question",
      name: "What expenses can dental associates, therapists, and hygienists claim on their tax return?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Dental associates, therapists, and hygienists can claim GDC registration fees, professional body membership, indemnity insurance, CPD courses, dental equipment and loupes, travel between multiple practice sites, and working-from-home costs for administrative work.",
      },
    },
    {
      "@type": "Question",
      name: "How can a specialist dental accountant help me pay less tax?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A specialist dental accountant understands tax reliefs specific to dentistry, from maximising pension contributions and claiming all allowable expenses, to advising on incorporation, capital allowances on dental equipment, and NHS pension annual allowance planning.",
      },
    },
    {
      "@type": "Question",
      name: "Do you help with dental practice acquisitions and sales?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. We support buyers and sellers end to end: financial due diligence, practice valuations, projections for the bank, tax-efficient deal structuring and post-completion accounting setup. We work alongside your solicitor and lender so the deal stacks up commercially as well as legally.",
      },
    },
    {
      "@type": "Question",
      name: "How much do your accountancy services cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We offer transparent, fixed-fee pricing with no hidden charges. Fees depend on complexity, from associate self-assessment returns to full practice accounts. Book a free consultation for a tailored quote.",
      },
    },
    {
      "@type": "Question",
      name: "Do you work with dental professionals across the whole of the UK?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, we work with dental associates, therapists, hygienists, and practice owners across England, Scotland, Wales, and Northern Ireland. As a cloud-based practice using Xero and digital tools, location is no barrier. We're based in Blackburn, Lancashire, but serve dental professionals nationwide.",
      },
    },
    {
      "@type": "Question",
      name: "What is Making Tax Digital and does it affect dentists?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Making Tax Digital (MTD) is HMRC's initiative requiring businesses to keep digital records and submit tax information using compatible software. Self-employed dentists earning above the threshold must comply with MTD for Income Tax. We help dental professionals set up MTD-compatible systems and ensure full compliance.",
      },
    },
    {
      "@type": "Question",
      name: "What is IR35 and how does it affect dental associates?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "IR35 is HMRC's legislation targeting disguised employment. Most dental associates are genuinely self-employed, but HMRC has increasingly scrutinised these arrangements. We review working arrangements, ensure associate agreements support self-employed status, and provide defence if HMRC raises questions.",
      },
    },

  ],
};

const Index = () => {
  const [showDeferredSections, setShowDeferredSections] = useState(false);

  useEffect(() => {
    const revealDeferredSections = () => setShowDeferredSections(true);

    if (typeof window === "undefined") return;

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(revealDeferredSections, { timeout: 1200 });

      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = setTimeout(revealDeferredSections, 450);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <Helmet>
        <title>RR Dental Accountants | Save Tax, Keep More Money</title>
        <meta name="description" content="Specialist accountants for dentists across the UK. We handle tax returns, dental bookkeeping and practice accounts so you focus on patients, not paperwork." />
        <link rel="canonical" href="https://dentalaccountancy.co.uk/" />
        <meta property="og:url" content="https://dentalaccountancy.co.uk/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="RR Dental Accountants | Save Tax, Keep More Money" />
        <meta property="og:description" content="Specialist accountants for dentists across the UK. We handle tax returns, dental bookkeeping and practice accounts so you focus on patients, not paperwork." />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <div className="min-h-screen">
        <Navbar />
        <main>
          <Hero />
          {showDeferredSections ? (
            <Suspense fallback={null}>
              <TrustStats />
              <PainPoints />
              <NearYouBand />
              <VideoSection />
              <Services />
              <HowItWorks />
              <Testimonials />
              <FAQ />
              <CityLinks />
              <LearningCenter />
              <LeadMagnets />
              <Contact />
            </Suspense>
          ) : null}
        </main>
        <Footer />
      </div>
      {showDeferredSections ? (
        <Suspense fallback={null}>
          <StickyMobileCTA />
        </Suspense>
      ) : null}
      
      
    </>
  );
};

export default Index;
